import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { apiClient } from '../services/apiClient';
import {
  clearPendingMfaToken,
  clearStoredAccessToken,
  getPendingMfaToken,
  isPendingMfaToken,
  setPendingMfaToken,
  TOKEN_STORAGE_KEY,
} from '../services/authTokenRefresh';
import { applyThemeToDocument, readStoredTheme, writeStoredTheme } from '../utils/themePreference';
import { getApiErrorMessage } from '../utils/apiErrors';
import { UserService, User as ApiUser } from '../services/api/userService';
import { CompanyInstance } from '../types/companyInstance';
import { Company } from '../types/company';
import { resolveCompanySelectionAfterLoad } from '../utils/resolveCompanySelectionAfterLoad';
import { mapUserCompaniesFromApi, type UserCompanyApiRow } from '../utils/mapUserCompaniesFromApi';
import { isDevRosterAuthRequired } from '../config/devRosterAuth';
import { SELECTED_COMPANY_KRYSPACE_SLUG_KEY } from '../config/kryspaceReadSource';
import { fetchUserCompanyRows } from '../services/fetchUserCompanyRows';
import { filterRosterMappedCompanies } from '../utils/rosterMappedCompanies';
import { filterReachableKryspaceCompanies } from '../utils/filterReachableKryspaceCompanies';
import { resolveAppUserRole } from '../utils/resolveAppUserRole';

export interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  isActive: boolean;
  role: string;
  permissions: string[];
  preferences: {
    defaultUIRole: 'architect' | 'operator' | 'coach' | 'integrator' | 'development' | 'marketfit';
    defaultEnvironment: 'Prime' | 'Company' | 'Sandbox';
    theme: 'light' | 'dark';
    notifications: boolean;
    /** IANA timezone from profile when backend supplies it */
    timezone?: string;
  };
  lastActive: string;
  avatar?: string;
  /** From API when available; unverified users cannot obtain a session when verification is enforced */
  emailVerified?: boolean;
  /** companies.id when linked (registration or profile) */
  companyId?: string;
  /** Subscription / intake gating from GET /auth/context (ISSUE-245) */
  onboarding?: OnboardingFlags;
}

/** Result of password login — MFA challenge when backend MFA_EMAIL_OTP_ENABLED is on. */
export type LoginResult =
  | { mfaRequired: false }
  | { mfaRequired: true; mfaStep: 'enroll' | 'challenge'; mfaEmailSent?: boolean };

/** Mirrors backend onboarding_service.build_onboarding_flags_for_user (snake_case on wire) */
export interface OnboardingFlags {
  subscriptionTier: string;
  snapshotOnly: boolean;
  foundationComplete: boolean;
  coreActive: boolean;
  /** Billing ≠ model: true when slug + imported artifact_items exist */
  modelReady: boolean;
  modelImportPresent: boolean;
  modelReadyCompanyIds: string[];
}

function mapOnboardingFlags(raw: unknown): OnboardingFlags | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const o = raw as Record<string, unknown>;
  const readyIds = Array.isArray(o.model_ready_company_ids)
    ? o.model_ready_company_ids.map((id) => String(id))
    : [];
  return {
    subscriptionTier: String(o.subscription_tier ?? 'none'),
    snapshotOnly: Boolean(o.snapshot_only),
    foundationComplete: Boolean(o.foundation_complete),
    coreActive: Boolean(o.core_active),
    modelReady: Boolean(o.model_ready),
    modelImportPresent: Boolean(o.model_import_present),
    modelReadyCompanyIds: readyIds,
  };
}

export interface RegisterInput {
  username: string;
  email: string;
  password: string;
  full_name?: string;
  company_name?: string;
  industry?: string;
  headcount?: number;
}

export interface RegisterResult {
  emailVerified: boolean;
  email: string;
  companyId?: string;
  companyInstanceId?: string;
}

export interface UserSession {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export type UserCompany = {
  instance: CompanyInstance;
  company: Company;
};

export interface SelectedCompany {
  id: string;
  name: string;
  instance_id?: string;
  kryspace_slug?: string;
}

interface UserContextType extends UserSession {
  // Simple user selection (no authentication)
  selectUser: (userId: string) => Promise<void>;
  getAvailableUsers: () => Promise<User[]>;
  
  // Company selection
  selectedCompany: SelectedCompany | null;
  availableCompanies: UserCompany[];
  /** Roster rows whose kryspace_slug failed manifest reachability probe */
  unreachableCompanies: UserCompany[];
  selectCompany: (companyId: string) => Promise<void>;
  /** Set company from Local Bridge (bypasses availableCompanies; for dashboard when sessionId in URL) */
  selectCompanyForLocalBridge: (companyId: string, companyName: string) => void;
  loadUserCompanies: () => Promise<UserCompany[]>;
  
  // Full authentication (for future use)
  /** Password login. When MFA is enabled, returns mfaRequired and does not authenticate until verifyMfaOtp. */
  login: (email: string, password: string) => Promise<LoginResult>;
  /** Complete email OTP after login returned mfaRequired. */
  verifyMfaOtp: (otp: string) => Promise<void>;
  /** Resend email OTP during pending MFA challenge. */
  resendMfaOtp: () => Promise<{ sent: boolean; cooldown_seconds?: number; error?: string }>;
  /** Creates account; does not issue a session. Caller shows verify-email vs sign-in messaging. */
  register: (input: RegisterInput) => Promise<RegisterResult>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => Promise<void>;
  updatePreferences: (preferences: Partial<User['preferences']>) => Promise<void>;
  /** Local theme (and other prefs) when `user` is null so header/theme can re-render. */
  guestPreferences: User['preferences'];
  refreshUser: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const GUEST_PREFERENCE_DEFAULTS: User['preferences'] = {
  defaultUIRole: 'operator',
  defaultEnvironment: 'Company',
  theme: 'light',
  notifications: true,
};

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [guestPreferences, setGuestPreferences] = useState<User['preferences']>(() => ({
    ...GUEST_PREFERENCE_DEFAULTS,
    theme: readStoredTheme() ?? 'light',
  }));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<SelectedCompany | null>(null);
  const [availableCompanies, setAvailableCompanies] = useState<UserCompany[]>([]);
  const [unreachableCompanies, setUnreachableCompanies] = useState<UserCompany[]>([]);
  const loadUserCompaniesGenerationRef = useRef(0);
  const selectedCompanyRef = useRef<SelectedCompany | null>(null);
  const availableCompaniesRef = useRef<UserCompany[]>([]);

  useEffect(() => {
    selectedCompanyRef.current = selectedCompany;
  }, [selectedCompany]);

  useEffect(() => {
    availableCompaniesRef.current = availableCompanies;
  }, [availableCompanies]);
  const checkSessionRef = useRef(false); // Prevent duplicate calls in React strict mode

  // Helper function to map API user to local User interface
  const mapApiUserToUser = useCallback((
    apiUser: ApiUser,
    getPermissionsForRole: (role: string) => string[],
    overrides?: Partial<Pick<User, 'permissions' | 'onboarding' | 'companyId' | 'role'>>,
  ): User => {
    // Honor API role / is_superuser first (demo name heuristics are fallback only).
    const role = overrides?.role ?? resolveAppUserRole(apiUser);

    // Split full_name into firstName and lastName if it exists
    const fullName = apiUser.full_name || apiUser.username;
    const nameParts = apiUser.full_name?.split(' ') || [];
    const firstName = nameParts.length > 0 ? nameParts[0] : undefined;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : undefined;

    // Use profile_data.preferences from backend when available
    const storedPrefs = apiUser.profile_data?.preferences as Partial<User['preferences']> | undefined;
    const defaultPrefs = {
      defaultUIRole: (role === 'admin' || role === 'superuser' ? 'architect' : role === 'coach' ? 'coach' : role === 'operator' ? 'operator' : 'architect') as User['preferences']['defaultUIRole'],
      defaultEnvironment: 'Company' as const,
      theme: 'light' as const,
      notifications: true,
    };

    const fromRole = getPermissionsForRole(role);
    // Superuser/admin always keep role-derived perms (incl. *) even if /auth/context returns a thin list.
    const perms =
      role === 'superuser' || role === 'admin' || role === 'platform_admin'
        ? fromRole
        : overrides?.permissions != null && overrides.permissions.length > 0
          ? overrides.permissions
          : fromRole;

    return {
      id: apiUser.id,
      username: apiUser.username,
      email: apiUser.email,
      firstName,
      lastName,
      fullName,
      isActive: apiUser.is_active,
      role: role,
      permissions: perms,
      preferences: {
        ...defaultPrefs,
        ...storedPrefs,
      },
      lastActive: new Date().toISOString(),
      emailVerified: apiUser.email_verified,
      companyId: overrides?.companyId ?? apiUser.company_id,
      onboarding: overrides?.onboarding,
    };
  }, []);

  // Helper function to get permissions for a role (using correct format: action:resource)
  const getPermissionsForRole = useCallback((role: string): string[] => {
    const normalized = (role ?? '').toLowerCase().replace(/-/g, '_');
    if (normalized === 'superuser') {
      return ['*']; // Wildcard for all permissions
    }
    if (normalized === 'admin' || normalized === 'platform_admin' || normalized === 'administrator') {
      return [
        'read:artifacts',
        'write:artifacts',
        'delete:artifacts',
        'read:processes',
        'write:processes',
        'delete:processes',
        'read:threads',
        'write:threads',
        'delete:threads',
        'execute:commands',
        'admin:commands',
        'manage:platform',
        'manage:companies',
        'manage:users',
        'manage:roles',
        'view:analytics',
      ];
    }
    if (normalized === 'architect' || normalized === 'manager') {
      return [
        'read:artifacts',
        'write:artifacts',
        'read:processes',
        'write:processes',
        'read:threads',
        'write:threads',
        'execute:commands',
        'view:analytics',
      ];
    }
    if (normalized === 'operator' || normalized === 'viewer') {
      return [
        'read:artifacts',
        'read:processes',
        'read:threads',
        'execute:commands',
      ];
    }
    // Default for 'user' and other roles
    return [
      'read:artifacts',
      'write:artifacts',
      'read:processes',
      'write:processes',
      'read:threads',
      'write:threads',
      'execute:commands',
    ];
  }, []);

  // Helper function to generate deterministic UUIDs for mock users
  const generateDeterministicUUID = useCallback((seed: string): string => {
    // Simple deterministic UUID v4 generation based on seed
    const hash = seed.split('').reduce((acc, char) => {
      acc = ((acc << 5) - acc) + char.charCodeAt(0);
      return acc & acc;
    }, 0);
    const hex = Math.abs(hash).toString(16).padStart(32, '0');
    return `${hex.slice(0,8)}-${hex.slice(8,12)}-4${hex.slice(13,16)}-${((hash & 0x3) | 0x8).toString(16)}${hex.slice(17,20)}-${hex.slice(20,32)}`;
  }, []);

  // Load user's companies (company instances with company details)
  const applyCompanySelectionResolution = useCallback(
    (resolution: ReturnType<typeof resolveCompanySelectionAfterLoad>) => {
      if (resolution.action === 'keep') {
        return;
      }
      if (resolution.action === 'clear') {
        setSelectedCompany(null);
        localStorage.removeItem('selectedCompanyId');
        localStorage.removeItem('selectedCompanyInstanceId');
        localStorage.removeItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY);
        return;
      }
      const companyToSelect: SelectedCompany = {
        id: resolution.selection.id,
        name: resolution.selection.name,
        instance_id: resolution.selection.instance_id,
        kryspace_slug: resolution.selection.kryspace_slug,
      };
      setSelectedCompany(companyToSelect);
      localStorage.setItem('selectedCompanyId', companyToSelect.id);
      if (resolution.selection.instance_id) {
        localStorage.setItem('selectedCompanyInstanceId', resolution.selection.instance_id);
      }
      if (companyToSelect.kryspace_slug) {
        localStorage.setItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY, companyToSelect.kryspace_slug);
      } else {
        localStorage.removeItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY);
      }
    },
    []
  );

  const loadUserCompanies = useCallback(async (): Promise<UserCompany[]> => {
    if (!user?.id) {
      setAvailableCompanies([]);
      setUnreachableCompanies([]);
      setSelectedCompany(null);
      return [];
    }

    const generation = ++loadUserCompaniesGenerationRef.current;

    try {
      const rows = await fetchUserCompanyRows(user.id);
      // Keep slug-less companies — new-user intake creates a company before
      // kryspace_slug exists; filtering them out broke prospector resume after re-login.
      const validCompanies = mapUserCompaniesFromApi(user.id, rows);

      if (generation !== loadUserCompaniesGenerationRef.current) {
        return validCompanies;
      }

      // Publish roster + selection immediately. Reachability probes can hang on
      // slow attention-manifest reads — awaiting them blocked Control Bridge.
      setAvailableCompanies(validCompanies);
      setUnreachableCompanies([]);

      const currentSelectedId = localStorage.getItem('selectedCompanyId');
      applyCompanySelectionResolution(
        resolveCompanySelectionAfterLoad(
          validCompanies,
          currentSelectedId,
          selectedCompanyRef.current
        )
      );

      // Annotate unreachable in the background; never gate the roster on probes.
      // Only probe companies that already have a slug (intake shells have none).
      void (async () => {
        try {
          const withSlug = isDevRosterAuthRequired()
            ? filterRosterMappedCompanies(validCompanies)
            : validCompanies;
          const filtered = await filterReachableKryspaceCompanies(withSlug);
          if (generation !== loadUserCompaniesGenerationRef.current) {
            return;
          }
          setUnreachableCompanies(filtered.unreachable);
        } catch (probeErr) {
          if (process.env.NODE_ENV === 'development') {
            console.warn(
              '[UserContext] Kryspace slug probe failed; keeping roster companies:',
              probeErr
            );
          }
        }
      })();

      return validCompanies;
    } catch (error) {
      if (generation !== loadUserCompaniesGenerationRef.current) {
        return [];
      }
      const status = (error as { response?: { status?: number } })?.response?.status;
      console.error('[UserContext] Error loading user companies:', error);
      if (status === 401) {
        setAvailableCompanies([]);
        setUnreachableCompanies([]);
        setSelectedCompany(null);
        localStorage.removeItem('selectedCompanyId');
        localStorage.removeItem('selectedCompanyInstanceId');
        localStorage.removeItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY);
      }
      return [];
    }
  }, [user?.id, applyCompanySelectionResolution]);

  // Select a company (header switcher) and align profile users.company_id
  const selectCompany = useCallback(async (companyId: string) => {
    const applyLocalSelection = (foundCompany: UserCompany) => {
      const companyToSelect: SelectedCompany = {
        id: foundCompany.company.id,
        name: foundCompany.company.name,
        instance_id: foundCompany.instance.id,
        kryspace_slug: foundCompany.company.kryspace_slug,
      };
      setSelectedCompany(companyToSelect);
      localStorage.setItem('selectedCompanyId', companyToSelect.id);
      if (foundCompany.instance.id) {
        localStorage.setItem('selectedCompanyInstanceId', foundCompany.instance.id);
      }
      if (companyToSelect.kryspace_slug) {
        localStorage.setItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY, companyToSelect.kryspace_slug);
      } else {
        localStorage.removeItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY);
      }
      return companyToSelect;
    };

    const persistProfileCompanyId = async (id: string) => {
      // Skip no-op writes when profile already points at this company
      const currentProfileId = user?.companyId;
      if (currentProfileId === id) {
        setUser((prev) => (prev ? { ...prev, companyId: id } : prev));
        return;
      }
      setUser((prev) => (prev ? { ...prev, companyId: id } : prev));
      try {
        const response = await apiClient.patch('/auth/user', { company_id: id });
        const updatedUser = mapApiUserToUser(response.data as ApiUser, getPermissionsForRole);
        setUser((prev) =>
          prev
            ? {
                ...updatedUser,
                // Keep client preference/onboarding state if the PATCH body omitted them
                preferences: prev.preferences,
                onboarding: prev.onboarding ?? updatedUser.onboarding,
                companyId: updatedUser.companyId ?? id,
              }
            : updatedUser
        );
      } catch (err) {
        console.warn(
          '[UserContext] Failed to persist users.company_id after company switch:',
          err
        );
      }
    };

    const foundCompany = availableCompanies.find(
      (uc) => uc.company.id === companyId
    );

    if (foundCompany) {
      applyLocalSelection(foundCompany);
      await persistProfileCompanyId(foundCompany.company.id);
      return;
    }

    await loadUserCompanies();
    const retry = availableCompaniesRef.current.find((uc) => uc.company.id === companyId);
    if (retry) {
      applyLocalSelection(retry);
      await persistProfileCompanyId(retry.company.id);
      return;
    }
    console.warn('[UserContext] Company not found in available companies:', companyId);
  }, [availableCompanies, loadUserCompanies, mapApiUserToUser, getPermissionsForRole, user?.companyId]);

  const selectCompanyForLocalBridge = useCallback((companyId: string, companyName: string) => {
    setSelectedCompany({ id: companyId, name: companyName });
    localStorage.setItem('selectedCompanyId', companyId);
    setUser((prev) => (prev ? { ...prev, companyId } : prev));
  }, []);

  // Define selectUser first so it can be used in useEffect
  const selectUser = useCallback(async (userId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Validate UUID format if it looks like it should be a UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const isUUID = uuidRegex.test(userId);
      
      // Try to fetch from API first if it's a UUID
      if (isUUID) {
        try {
          const apiUser = await UserService.getById(userId);
          
          // Convert API user to our User interface
          const user = mapApiUserToUser(apiUser, getPermissionsForRole);
          
            setUser(user);
            setIsAuthenticated(true);
            localStorage.setItem('selectedUserId', user.id);
            // Load companies for the user
            await loadUserCompanies();
            return;
        } catch (apiError: any) {
          // Stale localStorage UUID after DB reset: do not fall back to mock users (those IDs are not in DB).
          if (apiError.response?.status === 404) {
            localStorage.removeItem('selectedUserId');
            setUser(null);
            setIsAuthenticated(false);
            setError(
              'Stored user no longer exists. Clear site data or pick a user again — or run sql/137_seed_dev_demo_user.sql if the database has no users.'
            );
            return;
          }
          // Suppress CORS/network errors - these are expected backend configuration issues
          const isCorsError = apiError.code === 'ERR_NETWORK' || 
                             apiError.message?.includes('CORS') ||
                             apiError.message?.includes('Access-Control-Allow-Origin');
          if (!isCorsError) {
            console.warn('API user fetch failed, falling back to user list:', apiError);
          }
        }
      }
      
      // If userId is not a UUID, try to find by username/email in available users
      // Try to get available users from API first to get real UUIDs
      let listFetchFailed = false;
      let availableUsers: Awaited<ReturnType<typeof UserService.getAll>> | null = null;
      try {
        availableUsers = await UserService.getAll();
        if (availableUsers && availableUsers.length > 0) {
          // Try to find the user by ID (might be UUID or old numeric ID)
          let foundUser = availableUsers.find(u => u.id === userId);
          
          // If not found by exact ID, try to find by username or email if userId looks like a username
          if (!foundUser && userId && !userId.includes('-')) {
            // userId might be a username or old numeric ID, try to find by username
            foundUser = availableUsers.find(u => u.username === userId || u.email === userId);
          }
          
          if (foundUser) {
            // Convert API user to our User interface
            const user = mapApiUserToUser(foundUser, getPermissionsForRole);
            
            setUser(user);
            setIsAuthenticated(true);
            localStorage.setItem('selectedUserId', user.id);
            // Load companies for the user
            await loadUserCompanies();
            return;
          }
          // Users exist in DB but this id/username is unknown — do not use mock UUIDs
          localStorage.removeItem('selectedUserId');
          setUser(null);
          setIsAuthenticated(false);
          setError('User not found. Choose a user from the list or sign in.');
          return;
        }
      } catch (listError) {
        listFetchFailed = true;
        console.warn('Failed to get user list, using mock fallback:', listError);
      }

      // API reachable but no users: mock IDs would violate chat_threads FK — stop here.
      if (!listFetchFailed && availableUsers && availableUsers.length === 0) {
        localStorage.removeItem('selectedUserId');
        setUser(null);
        setIsAuthenticated(false);
        setError(
          'No users in the database. Register an account, or apply sql/137_seed_dev_demo_user.sql (demo_user / demo) for local Docker.'
        );
        return;
      }
      
      // Fallback to mock data only when the user list API failed (offline / misconfigured)
      const mockUsers: User[] = [
        {
          id: generateDeterministicUUID('admin'),
          username: 'admin',
          email: 'admin@krysalisplan.com',
          firstName: 'Krysalis',
          lastName: 'Administrator',
          fullName: 'Krysalis Administrator',
          isActive: true,
          role: 'admin',
          permissions: getPermissionsForRole('admin'),
          preferences: {
            defaultUIRole: 'architect',
            defaultEnvironment: 'Company',
            theme: 'light',
            notifications: true,
          },
          lastActive: new Date().toISOString(),
        },
        {
          id: generateDeterministicUUID('alex'),
          username: 'alex',
          email: 'alex@example.com',
          firstName: 'Alex',
          lastName: 'Operator',
          fullName: 'Alex Operator',
          isActive: true,
          role: 'operator',
          permissions: getPermissionsForRole('operator'),
          preferences: {
            defaultUIRole: 'operator',
            defaultEnvironment: 'Company',
            theme: 'light',
            notifications: true,
          },
          lastActive: new Date().toISOString(),
        },
      ];
      
      // Try to find user by ID, or by username/email if ID doesn't match
      let mockUser = mockUsers.find(u => u.id === userId);
      if (!mockUser && userId) {
        // If userId is not a UUID, try to match by username or email
        mockUser = mockUsers.find(u => u.username === userId || u.email === userId);
      }
      // Default to first user if no match
      mockUser = mockUser || mockUsers[0];
      
      setUser(mockUser);
      setIsAuthenticated(true);
      localStorage.setItem('selectedUserId', mockUser.id);
      // Load companies for the user
      await loadUserCompanies();
    } catch (err: any) {
      setError(err.message || 'Failed to select user');
      setIsAuthenticated(false);
      setUser(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [loadUserCompanies]);

  // Helper function to get auth token
  const getAuthToken = useCallback((): string | null => {
    return localStorage.getItem(TOKEN_STORAGE_KEY);
  }, []);

  // Helper function to clear auth token
  const clearAuthToken = useCallback(() => {
    clearStoredAccessToken();
    clearPendingMfaToken();
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    // Prevent duplicate calls in React strict mode
    if (checkSessionRef.current) {
      return;
    }
    checkSessionRef.current = true;

    const checkSession = async () => {
      // 1. Check if token exists before making any API calls
      const token = getAuthToken();
      if (token && isPendingMfaToken(token)) {
        clearAuthToken();
        setIsLoading(false);
        setIsAuthenticated(false);
        setUser(null);
        return;
      }
      if (!token) {
        // No token found - this is expected for demo/dev mode, skip silently
        setIsLoading(false);
        setIsAuthenticated(false);
        setUser(null);
        
        // Fallback: check for selected user ID (for non-authenticated mode)
        const selectedUserId = localStorage.getItem('selectedUserId');
        if (selectedUserId) {
          // Validate that selectedUserId is a UUID format
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
          if (uuidRegex.test(selectedUserId)) {
            try {
              await selectUser(selectedUserId);
              // loadUserCompanies is called within selectUser, so company selection will be handled there
            } catch (err) {
              console.error('[UserContext] User selection failed:', err);
              localStorage.removeItem('selectedUserId');
              setIsLoading(false);
            }
          } else {
            // Old non-UUID format, clear it and let user select a new one
            console.warn('[UserContext] Stored user ID is not a UUID format, clearing:', selectedUserId);
            localStorage.removeItem('selectedUserId');
            setIsLoading(false);
          }
        }
        return;
      }

      // 2. Token exists, try to get user info from API
      try {
        // Try to fetch from /auth/context first (includes permissions)
        // Note: baseURL already includes /api/v1, so we don't need to include it in the path
        let contextResponse;
        try {
          contextResponse = await apiClient.get('/auth/context');
        } catch (contextError: any) {
          // Fallback to /auth/me if context endpoint doesn't exist or returns an error
          if (process.env.NODE_ENV === 'development') {
            console.warn('[UserContext] /auth/context endpoint failed, falling back to /auth/me:', {
              status: contextError?.response?.status,
              message: contextError?.response?.data?.detail || contextError?.message
            });
          }
          contextResponse = null;
        }

        if (contextResponse?.data) {
          const payload = contextResponse.data as {
            user?: ApiUser;
            context?: { onboarding?: unknown };
            permissions?: string[];
          };
          const apiUser = payload.user ?? (payload as unknown as ApiUser);
          if (!apiUser?.id) {
            throw new Error('Invalid /auth/context payload: missing user');
          }

          const permissions: string[] = payload.user?.permissions ?? payload.permissions ?? [];
          const role = resolveAppUserRole(apiUser);

          const onboarding = mapOnboardingFlags(payload.context?.onboarding);
          const user = mapApiUserToUser(apiUser, getPermissionsForRole, {
            role,
            permissions: permissions.length > 0 ? permissions : undefined,
            onboarding,
          });
          if (role === 'superuser' || role === 'admin') {
            user.preferences.defaultUIRole = 'architect';
          }

          setUser(user);
          setIsAuthenticated(true);
          setIsLoading(false);
          // Companies will be loaded via useEffect when user state updates
          return;
        }

        // Fallback to /auth/me if context endpoint failed
        const response = await apiClient.get('/auth/me');
        if (response.data) {
          const apiUser = response.data;
          const user = mapApiUserToUser(apiUser, getPermissionsForRole);
          
          setUser(user);
          setIsAuthenticated(true);
          setIsLoading(false);
          // Companies will be loaded via useEffect when user state updates
          return;
        }
      } catch (err: any) {
        // 3. Handle 401 gracefully
        if (err.response?.status === 401) {
          if (process.env.NODE_ENV === 'development') {
            console.log('[UserContext] Token invalid/expired, clearing auth');
          }
          clearAuthToken();
          setUser(null);
          setIsAuthenticated(false);

          // Legacy dev-only: auto-identity via /dev/users/me (demo_user) when roster auth is off
          if (!isDevRosterAuthRequired()) {
            try {
              const devResponse = await apiClient.get('/dev/users/me');
              if (devResponse.data) {
                if (process.env.NODE_ENV === 'development') {
                  console.log('[UserContext] Using dev endpoint for user info');
                }
                const apiUser = devResponse.data;
                const user = mapApiUserToUser(apiUser, getPermissionsForRole);

                setUser(user);
                setIsAuthenticated(true);
                setIsLoading(false);
                return;
              }
            } catch (devError) {
              console.log('[UserContext] User needs to login');
            }
          } else if (process.env.NODE_ENV === 'development') {
            console.log('[UserContext] Dev roster auth required — sign in via /login');
          }
        } else {
          // Other errors
          console.error('[UserContext] Error checking session:', err);
        }
        
        // Token invalid or expired, clear it
        clearAuthToken();
      }

      // Fallback: check for selected user ID (for non-authenticated mode)
      const selectedUserId = localStorage.getItem('selectedUserId');
      if (selectedUserId) {
        // Validate that selectedUserId is a UUID format
        const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (uuidRegex.test(selectedUserId)) {
          try {
            await selectUser(selectedUserId);
            // loadUserCompanies is called within selectUser, so company selection will be handled there
          } catch (err) {
            console.error('[UserContext] User selection failed:', err);
            localStorage.removeItem('selectedUserId');
            setIsLoading(false);
          }
        } else {
          // Old non-UUID format, clear it and let user select a new one
          console.warn('[UserContext] Stored user ID is not a UUID format, clearing:', selectedUserId);
          localStorage.removeItem('selectedUserId');
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };

    void checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentionally empty deps; checkSession runs only once on mount
  }, []);

  // Load companies when user changes
  useEffect(() => {
    if (user?.id) {
      void loadUserCompanies();
    } else {
      setAvailableCompanies([]);
      setSelectedCompany(null);
    }
  }, [user?.id, loadUserCompanies]);

  const getAvailableUsers = useCallback(async (): Promise<User[]> => {
    try {
      // Try to fetch from API first
      try {
        const apiUsers = await UserService.getAll();
        
        // Convert API users to our User interface
        return apiUsers.map((apiUser) => mapApiUserToUser(apiUser, getPermissionsForRole));
      } catch (apiError) {
        console.warn('API users fetch failed, falling back to mock data:', apiError);
      }
      
      // Fallback to mock data if API fails - use proper UUIDs
      const mockUsers: User[] = [
        {
          id: generateDeterministicUUID('admin'),
          username: 'admin',
          email: 'admin@krysalis.com',
          firstName: 'Admin',
          lastName: 'User',
          fullName: 'Admin User',
          isActive: true,
          role: 'admin',
          permissions: getPermissionsForRole('admin'),
          preferences: {
            defaultUIRole: 'architect',
            defaultEnvironment: 'Company',
            theme: 'light',
            notifications: true,
          },
          lastActive: new Date().toISOString(),
        },
        {
          id: generateDeterministicUUID('architect'),
          username: 'architect',
          email: 'architect@krysalis.com',
          firstName: 'System',
          lastName: 'Architect',
          fullName: 'System Architect',
          isActive: true,
          role: 'architect',
          permissions: getPermissionsForRole('architect'),
          preferences: {
            defaultUIRole: 'architect',
            defaultEnvironment: 'Prime',
            theme: 'light',
            notifications: true,
          },
          lastActive: new Date().toISOString(),
        },
        {
          id: generateDeterministicUUID('operator'),
          username: 'operator',
          email: 'operator@krysalis.com',
          firstName: 'Process',
          lastName: 'Operator',
          fullName: 'Process Operator',
          isActive: true,
          role: 'operator',
          permissions: ['read', 'execute'],
          preferences: {
            defaultUIRole: 'operator',
            defaultEnvironment: 'Company',
            theme: 'light',
            notifications: false,
          },
          lastActive: new Date().toISOString(),
        },
      ];
      
      return mockUsers;
    } catch (error) {
      console.error('Failed to fetch users:', error);
      return [];
    }
  }, []);

  const establishSessionFromAccessToken = useCallback(
    async (accessToken: string) => {
      localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
      clearPendingMfaToken();
      try {
        let apiUser: ApiUser;
        let overrides: Partial<Pick<User, 'permissions' | 'onboarding' | 'companyId'>> | undefined;
        try {
          const ctxRes = await apiClient.get('/auth/context');
          const payload = ctxRes.data as {
            user?: ApiUser;
            context?: { onboarding?: unknown };
          };
          if (!payload.user?.id) {
            throw new Error('missing user in context response');
          }
          apiUser = payload.user;
          const perms = payload.user.permissions ?? [];
          const onboarding = mapOnboardingFlags(payload.context?.onboarding);
          overrides = {
            permissions: perms.length > 0 ? perms : undefined,
            onboarding,
          };
        } catch {
          const userResponse = await apiClient.get('/auth/me');
          apiUser = userResponse.data as ApiUser;
          overrides = undefined;
        }

        const mapped = mapApiUserToUser(apiUser, getPermissionsForRole, overrides);
        setUser(mapped);
        setIsAuthenticated(true);
      } catch (userErr: unknown) {
        console.warn('Could not fetch user data after login:', userErr);
        setIsAuthenticated(true);
      }
    },
    [mapApiUserToUser, getPermissionsForRole]
  );

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    // Do not toggle UserContext.isLoading — EnhancedAppRoutes unmounts <Routes> while
    // isLoading, which wipes RegisterPage/LoginPage MFA challenge state after OTP is emailed.
    setError(null);

    try {
      const response = await apiClient.post('/auth/login', {
        username: email,
        password: password,
      });
      const {
        access_token: accessToken,
        mfa_required: mfaRequired,
        mfa_step: mfaStep,
        mfa_email_sent: mfaEmailSent,
      } = response.data as {
        access_token?: string;
        mfa_required?: boolean;
        mfa_step?: string;
        mfa_email_sent?: boolean;
      };

      if (!accessToken) {
        throw new Error('No access token received');
      }

      if (mfaRequired) {
        clearStoredAccessToken();
        setPendingMfaToken(accessToken);
        setIsAuthenticated(false);
        setUser(null);
        const step = mfaStep === 'enroll' ? 'enroll' : 'challenge';
        return { mfaRequired: true, mfaStep: step, mfaEmailSent: mfaEmailSent !== false };
      }

      await establishSessionFromAccessToken(accessToken);
      return { mfaRequired: false };
    } catch (err: unknown) {
      const errorMessage = getApiErrorMessage(err, 'Login failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [establishSessionFromAccessToken]);

  const verifyMfaOtp = useCallback(async (otp: string) => {
    // Same as login: keep route tree mounted so the OTP form is not destroyed mid-submit.
    setError(null);
    const pending = getPendingMfaToken();
    if (!pending) {
      const message = 'MFA challenge expired. Sign in again.';
      setError(message);
      throw new Error(message);
    }
    try {
      const response = await apiClient.post(
        '/auth/mfa/verify-email-otp',
        { otp: otp.trim() },
        { headers: { Authorization: `Bearer ${pending}` } }
      );
      const accessToken = (response.data as { access_token?: string }).access_token;
      if (!accessToken) {
        throw new Error('No access token received');
      }
      await establishSessionFromAccessToken(accessToken);
    } catch (err: unknown) {
      const errorMessage = getApiErrorMessage(err, 'Invalid or expired verification code');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [establishSessionFromAccessToken]);

  const resendMfaOtp = useCallback(async () => {
    const pending = getPendingMfaToken();
    if (!pending) {
      throw new Error('MFA challenge expired. Sign in again.');
    }
    const response = await apiClient.post(
      '/auth/mfa/resend-email-otp',
      {},
      { headers: { Authorization: `Bearer ${pending}` } }
    );
    return response.data as { sent: boolean; cooldown_seconds?: number; error?: string };
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<RegisterResult> => {
    setError(null);
    try {
      const response = await apiClient.post<{
        email_verified?: boolean;
        email: string;
        company_id?: string;
        company_instance_id?: string;
      }>('/auth/register', {
        username: input.username,
        email: input.email,
        password: input.password,
        full_name: input.full_name,
        company_name: input.company_name,
        industry: input.industry,
        headcount: input.headcount,
      });
      const data = response.data;
      return {
        emailVerified: Boolean(data.email_verified),
        email: data.email,
        companyId: data.company_id,
        companyInstanceId: data.company_instance_id,
      };
    } catch (err: unknown) {
      const msg = getApiErrorMessage(err, 'Registration failed');
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    setSelectedCompany(null);
    setAvailableCompanies([]);
    clearAuthToken();
    localStorage.removeItem('selectedUserId');
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('selectedCompanyInstanceId');
    localStorage.removeItem(SELECTED_COMPANY_KRYSPACE_SLUG_KEY);
    setError(null);
  }, [clearAuthToken]);

  const updateUser = useCallback(async (updates: Partial<User>) => {
    if (!user) return;
    
    try {
      const payload: Record<string, unknown> = { ...updates };
      if (Object.prototype.hasOwnProperty.call(updates, 'companyId')) {
        payload.company_id = updates.companyId;
        delete payload.companyId;
      }
      const response = await apiClient.patch('/auth/user', payload);
      // Map API response (with profile_data) to User format (with preferences)
      const updatedUser = mapApiUserToUser(response.data as ApiUser, getPermissionsForRole);
      if (updates.preferences) {
        updatedUser.preferences = {
          ...updatedUser.preferences,
          ...updates.preferences,
        };
      }
      setUser(updatedUser);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Update failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [user, mapApiUserToUser, getPermissionsForRole]);

  const updatePreferences = useCallback(async (preferences: Partial<User['preferences']>) => {
    if (preferences.theme) {
      writeStoredTheme(preferences.theme);
      applyThemeToDocument(preferences.theme);
    }

    setGuestPreferences((prev) => ({ ...prev, ...preferences }));

    if (!user) return;

    const updatedPreferences = { ...user.preferences, ...preferences };
    setUser({ ...user, preferences: updatedPreferences });

    try {
      await updateUser({ preferences: updatedPreferences });
    } catch (err) {
      console.warn('[UserContext] Failed to persist preferences', err);
    }
  }, [user, updateUser]);

  const refreshUser = useCallback(async () => {
    // Check if token exists before making the request
    const token = getAuthToken();
    if (!token) {
      console.log('[UserContext] No token found, cannot refresh user');
      setUser(null);
      setIsAuthenticated(false);
      setError('No authentication token');
      return;
    }

    try {
      let apiUser: ApiUser;
      let overrides: Partial<Pick<User, 'permissions' | 'onboarding' | 'companyId'>> | undefined;
      try {
        const ctxRes = await apiClient.get('/auth/context');
        const payload = ctxRes.data as {
          user?: ApiUser;
          context?: { onboarding?: unknown };
        };
        if (!payload.user?.id) {
          throw new Error('missing user in context response');
        }
        apiUser = payload.user;
        const perms = payload.user.permissions ?? [];
        const onboarding = mapOnboardingFlags(payload.context?.onboarding);
        overrides = {
          permissions: perms.length > 0 ? perms : undefined,
          onboarding,
        };
      } catch {
        const response = await apiClient.get('/auth/me');
        apiUser = response.data as ApiUser;
        overrides = undefined;
      }

      const user = mapApiUserToUser(apiUser, getPermissionsForRole, overrides);

      setUser(user);
      setIsAuthenticated(true);
      setError(null);
      // Companies will be loaded via useEffect when user state updates
    } catch (err: any) {
      if (err.response?.status === 401) {
        console.log('[UserContext] Token invalid/expired during refresh, clearing auth');
        clearAuthToken();
        setUser(null);
        setIsAuthenticated(false);
        setError('Session expired');
      } else {
        setError('Failed to refresh user');
        console.error('[UserContext] Error refreshing user:', err);
      }
      throw err;
    }
  }, [getAuthToken, clearAuthToken, mapApiUserToUser, getPermissionsForRole]);

  const hasPermission = useCallback((permission: string) => {
    if (!user?.permissions) return false;
    
    // Superuser has all permissions (wildcard)
    if (user.permissions.includes('*') || user.role === 'superuser') {
      return true;
    }
    
    // Check exact permission match
    if (user.permissions.includes(permission)) {
      return true;
    }
    
    return false;
  }, [user]);

  const hasRole = useCallback((role: string) => {
    return user?.role === role;
  }, [user]);

  const value: UserContextType = {
    user,
    isAuthenticated,
    isLoading,
    error,
    selectUser,
    getAvailableUsers,
    selectedCompany,
    availableCompanies,
    unreachableCompanies,
    selectCompany,
    selectCompanyForLocalBridge,
    loadUserCompanies,
    login,
    verifyMfaOtp,
    resendMfaOtp,
    register,
    logout,
    updateUser,
    updatePreferences,
    guestPreferences,
    refreshUser,
    hasPermission,
    hasRole,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

// Hook for getting user preferences with defaults
// UX Principle #2: Execution Mode (operator) is the default
export const useUserPreferences = () => {
  const { user, guestPreferences, updatePreferences } = useUser();

  const preferences = user?.preferences ?? guestPreferences;

  return {
    preferences,
    updatePreferences,
  };
};
